import { isValidElement, type CSSProperties, type ReactNode } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { type Components } from '../../../core/types/markdown/react-markdown.js';
import type { SimpleTableV2ColumnDef } from '../../../tables/SimpleTableV2/public.api.js';
import { SimpleTableV2 } from '../../../tables/SimpleTableV2/SimpleTableV2.js';
import { getChildren } from '../utils.js';

/** Get text alignment from style */
function getTextAlignment(
  cell: ReactNode,
): 'left' | 'center' | 'right' | undefined {
  return isValidElement<{ style?: CSSProperties }>(cell)
    ? (cell?.props?.style?.textAlign as 'left' | 'center' | 'right')
    : undefined;
}

export const tableMappings: Components = {
  table: ({ children, ...props }) => {
    if (children.length === 0) {
      return null;
    }

    // map thead>tr>td to columns
    const tHead = children[0];
    const tHeadRow = getChildren(tHead)[0];
    const columns: SimpleTableV2ColumnDef<(typeof data)[number]>[] =
      getChildren(tHeadRow).map((cell, index) => ({
        header: () => <>{getChildren(cell)}</>,
        // eslint-disable-next-line react/jsx-no-useless-fragment
        cell: ({ value }) => <>{value}</>,
        accessor: String(index),
        id: String(index),
        alignment: getTextAlignment(cell),
      }));

    // map tbody>tr>td to data
    const tBody = children[1];
    const data = getChildren(tBody).map((row) => ({
      ...getChildren(row).map((cell) => getChildren(cell)),
    }));

    return (
      <div {...props}>
        <SimpleTableV2
          style={{
            marginTop: Spacings.Size24,
            marginBottom: Spacings.Size24,
          }}
          columns={columns}
          data={data}
          variant={{
            contained: true,
            verticalDividers: true,
            rowDensity: 'default',
          }}
        />
      </div>
    );
  },
};
